import { Component, type ErrorInfo, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'

/**
 * A render error in one part of the page stays in that part: the card says
 * so and offers another go, instead of React unmounting the whole app to a
 * blank screen (there was no boundary before Nov 4 2026). State and data
 * are untouched; "Try again" simply renders the part once more.
 */
export class ErrorBoundary extends Component<
  { children: ReactNode; label?: string },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null }

  static getDerivedStateFromError(error: Error) {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Render error', error, info.componentStack)
  }

  render() {
    if (this.state.error === null) return this.props.children
    return (
      <div className="card flex flex-col items-start gap-3 p-4 text-sm">
        <p className="text-negative">
          {this.props.label ?? 'This part'} could not be shown.
        </p>
        <p className="text-xs text-muted">{this.state.error.message}</p>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => this.setState({ error: null })}
        >
          Try again
        </button>
      </div>
    )
  }
}

/** the page's boundary: a new page starts clean, so an error on one never follows to the next */
export function RouteBoundary({ children }: { children: ReactNode }) {
  const { pathname } = useLocation()
  return (
    <ErrorBoundary key={pathname} label="This page">
      {children}
    </ErrorBoundary>
  )
}
